import { useState } from 'react'
import { Button, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { StockItemFormModal } from '@/features/inventory/StockItemFormModal'
import {
  STOCK_ITEM_ACTIVE_FILTER,
  STOCK_ITEM_WITH_INACTIVE,
  fetchStockItemPage,
  stockItemActiveOnly,
  type StockItem,
} from '@/features/inventory/stockApi'

/**
 * Mollar katalogi — MIQDORLI turlar (`inventory.stock_item`).
 *
 * <h2>⚠️ JIHOZLAR SAHIFASI BILAN CHALKASHTIRMANG</h2>
 * `/inventory` — seriya raqamli FIZIK NUSXALAR (router, ONU). Bu yerda
 * esa TUR: "UTP-5E kabel, metr". Ular ikki xil model va ataylab
 * birlashtirilmagan — 500 metr kabelning seriya raqami yo'q va bo'lishi
 * ham mumkin emas (`QARORLAR.md` §20.8, `StockItem` javadoc'i).
 *
 * <h2>⚠️ QOLDIQ USTUNI YO'Q</h2>
 * Qoldiq har OMBORDA alohida va u boshqa sahifada. Bu yerga "jami
 * qoldiq" ustunini qo'shish vasvasali edi va rad etildi: u
 * ko'rinishga qarab o'zgaradigan (doiraga bog'liq) son bo'lardi va
 * operator uni tashkilotning haqiqiy zaxirasi deb o'qirdi.
 *
 * <h2>⚠️ SAHIFALASH, QIDIRUV VA SARALASH — SERVERDA (2026-08-20)</h2>
 * `StockResource#items` endi `page`/`size`/`search`/`sortField`/
 * `sortOrder` ni ham oladi va `{rows,total,page,size}` qaytaradi.
 * Shuning uchun `localList` bu sahifadan olib tashlandi: u TO'LIQ
 * massiv ustida ishlashga mo'ljallangan va sahifalangan ro'yxatda
 * faqat joriy sahifani saralab, yolg'on tartib berardi.
 *
 * <h2>⚠️ «Chegara qo'yilgan / qo'yilmagan» FILTRI OLIB TASHLANDI</h2>
 * Serverda bunday filtr yo'q. Sahifalangan ro'yxatda uni mijozda
 * qilish faqat joriy sahifani toraytirardi va «chegarasiz mol yo'q»
 * degan yolg'on javob berardi. O'rniga «Eng kam miqdor» ustuni
 * SARALANADI (`MIN_QUANTITY`) — chegarasiz mollar bir chekkaga
 * yig'iladi va bu butun ro'yxatga tegishli.
 *
 * <h2>⚠️ SUKUT BO'YICHA FAQAT FAOL MOLLAR (egasi qarori 2026-08-20)</h2>
 * «Yopilganlar qoidasi»: nofaol mol ro'yxatda ko'rinmaydi va u holat
 * tanlagichi orqali qaytariladi. Bayroq `stockItemActiveOnly` da bir
 * joyda hisoblanadi va SERVERGA ketadi (`activeOnly`).
 *
 * ⚠️ «Faqat nofaollari» degan band YO'Q va bo'lmaydi ham: serverda
 * bunday so'rov yo'q, sahifalangan ro'yxatda esa uni mijozda yasash
 * faqat joriy sahifani toraytirardi. Shuning uchun ikkinchi band —
 * «Nofaollari bilan», ya'ni ROSTDAN kengaytirilgan so'rov.
 *
 * <h2>⚠️ SUKUT TARTIB O'ZGARDI</h2>
 * Ilgari server `sku` bo'yicha qaytarardi, endi `CREATED_AT DESC` —
 * oxirgi qo'shilgan mol tepada. SKU tartibi kerak bo'lsa «Nomi» ustuni
 * bosiladi (u `SKU` enumiga emas, `NAME` ga bog'langan) yoki jadval
 * sozlamalaridan SKU ustuni ochiladi.
 */
export function StockItemsPage() {
  const { t } = useTranslation()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<StockItem | null>(null)

  /*
    ⚠️ «Faol / nofaol» tanlagichi jadval holatining O'ZINI yozadi —
    `DataTable` o'sha manbadan o'qiydi va `activeOnly` serverga shundan
    ketadi. Alohida `useState` ikkinchi haqiqat manbai bo'lardi.
  */
  const { state: tableState, patch: patchTable } = useTableUrlState()
  const activeOnly = stockItemActiveOnly(tableState)

  const columns: ColumnsType<StockItem> = [
    {
      key: 'name',
      title: t('stock.item.name'),
      sorter: true,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong={row.active} type={row.active ? undefined : 'secondary'}>
            {row.name}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }} copyable={{ text: row.sku }}>
            {row.sku}
          </Typography.Text>
        </Space>
      ),
    },
    { key: 'unit', title: t('stock.item.unit'), width: 120, render: (_, row) => row.unit },
    {
      key: 'minQuantity',
      title: t('stock.item.minQuantity'),
      width: 180,
      sorter: true,
      // ⚠️ `0` va "yo'q" FARQLANADI: nol chegara "ogohlantirish
      // kerak emas" degani, bo'sh katak esa "sozlanmagan" degan
      // taassurot qoldirardi.
      render: (_, row) =>
        row.minQuantity === 0
          ? t('stock.item.minQuantityOff')
          : `${String(row.minQuantity)} ${row.unit}`,
    },
    {
      key: 'active',
      title: t('stock.item.active'),
      width: 130,
      render: (_, row) =>
        row.active ? (
          <Tag color="green">{t('stock.item.stateActive')}</Tag>
        ) : (
          <Tag>{t('stock.item.stateInactive')}</Tag>
        ),
    },
    {
      key: 'createdAt',
      title: t('common.createdAt'),
      width: 180,
      sorter: true,
      // Standart tartib shu ustun bo'yicha — ko'rsatilmasa qatorlarning
      // joylashuvi tushuntirilmagan bo'lardi.
      render: (_, row) =>
        row.createdAt === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
        ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'inventory.stock.write',
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.stockItems')}
        description={t('pageIntro.stockItems')}
        extra={
          <Can permission="inventory.stock.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('stock.item.createTitle')}
            </Button>
          </Can>
        }
      />

      {/* ⚠️ Bu yerda SAQLASH xatosi uchun banner YO'Q va bo'lmasligi ham
          kerak: u oynaning ICHIDA ko'rsatiladi (`StockItemFormModal`).
          Ro'yxat xatosini esa `DataTable` o'zi chizadi. */}
      <DataTable<StockItem>
        queryKey={queryKeys.stock.itemPage}
        fetchPage={fetchStockItemPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="stock-items"
        stickyFirstColumn
        searchable
        searchPlaceholder={t('stock.item.searchPlaceholder')}
        size="small"
        scrollX={1060}
        /* ⚠️ Nofaollar YASHIRILGAN holatda «katalog bo'sh» deyish yolg'on
           bo'lardi: nofaol mollar turgan bo'lishi mumkin va ular bir
           bosishda ochiladi. */
        emptyTitle={activeOnly ? t('listView.activeOnlyEmpty') : t('stock.item.empty')}
        emptyDescription={
          activeOnly ? t('listView.activeOnlyEmptyHint') : t('stock.item.emptyHint')
        }
        {...(activeOnly
          ? {
              emptyAction: {
                label: t('stock.item.withInactive'),
                onClick: () =>
                  patchTable({
                    page: 1,
                    filters: {
                      ...tableState.filters,
                      [STOCK_ITEM_ACTIVE_FILTER]: [STOCK_ITEM_WITH_INACTIVE],
                    },
                  }),
              },
            }
          : {})}
        toolbarExtra={
          /*
            ⚠️ IKKI QIYMAT, UCHTA EMAS. Serverda faqat `activeOnly`
            bayrog'i bor — «faqat nofaollari» degan filtr YO'Q. Uchinchi
            bandni qoldirish sahifalangan ro'yxatda faqat joriy sahifani
            toraytirardi va operator «nofaol mol yo'q» deb o'ylardi.

            ⚠️ SUKUT — «Faqat faollari» (egasi qarori 2026-08-20). URL'da
            hech narsa turmasa ham so'rovga `activeOnly=true` ketadi;
            nofaol mollar ikkinchi band bilan qaytariladi va bu ROSTDAN
            serverga boradi — `stockItemActiveOnly` bir joyda qaror
            qiladi va uni qorovul CHAQIRIB o'lchaydi.

            ⚠️ Sahifa BIRINCHIGA qaytariladi: 5-sahifada turib ro'yxatni
            toraytirsa operator bo'sh ekran ko'rib «mol yo'q» degan
            xulosaga kelardi.
          */
          <Select<'active' | 'withInactive'>
            value={activeOnly ? 'active' : 'withInactive'}
            onChange={(value) =>
              patchTable({
                page: 1,
                filters: {
                  ...tableState.filters,
                  [STOCK_ITEM_ACTIVE_FILTER]:
                    value === 'active' ? [] : [STOCK_ITEM_WITH_INACTIVE],
                },
              })
            }
            aria-label={t('listView.statusFilter')}
            style={{ width: 200 }}
            options={[
              { value: 'active', label: t('stock.item.activeOnly') },
              { value: 'withInactive', label: t('stock.item.withInactive') },
            ]}
          />
        }
      />

      <StockItemFormModal open={formOpen} item={editing} onClose={() => setFormOpen(false)} />
    </>
  )
}
